import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import YCodeThumbnail from '../components/YCodeThumbnail';
import { API_ENDPOINTS } from '../config/api';
import './Courses.css';

const Courses = () => {
  const [courses, setCourses] = useState([]);

  // GST calculation helper
  const GST_RATE = 0.18; // 18%
  const calculateGST = (price) => {
    return parseFloat((price * GST_RATE).toFixed(2));
  };
  const calculateTotal = (price) => {
    return parseFloat((price + calculateGST(price)).toFixed(2));
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.COURSES.BASE);
      if (response.ok) {
        const data = await response.json();
        setCourses(data);
      } else {
        setError('Failed to load courses');
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      setError('Unable to connect to server. Please check if the backend is running.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center pt-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-400 mx-auto mb-6"></div>
          <div className="text-white text-2xl font-medium mb-2">Loading Amazing Courses...</div>
          <div className="text-gray-400 text-lg">Preparing your learning journey</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center pt-20">
        <div className="text-center">
          <div className="text-6xl mb-6">⚠️</div>
          <div className="text-red-400 text-2xl font-semibold mb-4">{error}</div>
          <p className="text-gray-400 text-lg mb-8 max-w-md mx-auto">
            We're having trouble loading the courses. Please check your connection and try again.
          </p>
          <button 
            onClick={fetchCourses}
            className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-8 py-3 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            🔄 Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 pt-20">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-teal-900/30 via-cyan-900/30 to-teal-900/30 border-b border-teal-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-2">
                <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">
                  Programming Courses
                </span>
              </h1>
              <p className="text-gray-300 text-sm sm:text-base">
                Comprehensive courses designed by industry experts
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20">
                <div className="text-xs text-gray-400 mb-1">Available</div>
                <div className="text-lg font-bold text-teal-300">
                  {courses.length} {courses.length === 1 ? 'Course' : 'Courses'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Courses Section */}
      <div id="courses-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-16">
        {courses.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-8xl mb-8 animate-bounce">📚</div>
            <h2 className="text-4xl font-bold text-white mb-6">No Courses Available Yet</h2>
            <p className="text-gray-300 text-xl mb-8 max-w-2xl mx-auto">
              We're working hard to bring you amazing programming courses. 
              Check back soon for exciting content that will transform your coding skills!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/ide"
                className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                💻 Try Our IDE
              </Link>
              <button 
                onClick={fetchCourses}
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                🔄 Refresh
              </button>
            </div>
          </div>
        ) : (
          <>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map((course) => (
                <Link
                  key={course.id}
                  to={`/courses/${course.id}`}
                  className="group bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl border border-white/20 hover:border-teal-400/50 overflow-hidden transition-all duration-300 hover:shadow-xl"
                >
                  {/* Course Image */}
                  <div className="h-48 sm:h-56 bg-black relative overflow-hidden flex items-center justify-center">
                    {course.imageUrl ? (
                      <img 
                        src={course.imageUrl} 
                        alt={course.title}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-500 via-cyan-500 to-blue-500">
                        <span className="text-5xl">📚</span>
                      </div>
                    )}
                    {/* Course Type Badge - Top Left */}
                    <div className="absolute top-3 left-3">
                      <span className={`${
                        course.courseType === 'LIVE' 
                          ? 'bg-pink-500' 
                          : 'bg-purple-500'
                      } text-white px-2.5 py-1 rounded-md text-xs font-semibold shadow-lg`}>
                        {course.courseType === 'LIVE' ? '🔴 LIVE' : '🎬 RECORDED'}
                      </span>
                    </div>
                    {/* Discount Badge - Top Right */}
                    {course.isOnSale && course.discountPercentage && (
                      <div className="absolute top-3 right-3">
                        <span className="bg-red-500 text-white px-2.5 py-1 rounded-md text-xs font-semibold shadow-lg">
                          {course.discountPercentage}% OFF
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Course Content */}
                  <div className="p-5">
                    {/* Title */}
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-teal-300 transition-colors line-clamp-2">
                      {course.title}
                    </h3>
                    
                    {/* Description - Brief */}
                    <p className="text-gray-400 text-sm mb-4 line-clamp-2 leading-relaxed">
                      {course.description}
                    </p>
                    
                    {/* Key Info - Compact */}
                    <div className="flex items-center gap-4 mb-4 text-xs text-gray-400 pb-4 border-b border-white/10">
                      {course.instructor && (
                        <div className="flex items-center gap-1.5">
                          <span>👨‍🏫</span>
                          <span className="truncate max-w-[100px]">{course.instructor}</span>
                        </div>
                      )}
                      {course.courseType === 'RECORDED' && course.durationHours && (
                        <div className="flex items-center gap-1.5">
                          <span>⏱️</span>
                          <span>{course.durationHours}h</span>
                        </div>
                      )}
                      {course.courseType === 'LIVE' && course.totalSessions && (
                        <div className="flex items-center gap-1.5">
                          <span>🎯</span>
                          <span>{course.totalSessions} sessions</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Price - Clean Display */}
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        {course.isOnSale && course.originalPrice && (
                          <span className="text-gray-500 line-through text-xs mb-0.5">
                            ₹{course.originalPrice}
                          </span>
                        )}
                        <div className="text-white font-bold text-lg">
                          ₹{calculateTotal(course.price)}
                        </div>
                        <div className="text-gray-400 text-xs">
                          incl. GST
                        </div>
                      </div>
                      <div className="text-teal-400 font-semibold text-sm group-hover:text-teal-300 transition-colors">
                        View Details →
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            
            {/* Call to Action */}
            <div className="text-center mt-20">
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-12 border border-white/20">
                <h3 className="text-3xl font-bold text-white mb-4">Practice What You Learn</h3>
                <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                  Reinforce your learning with our curated question packages and hands-on coding challenges.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link
                    to="/question-packages"
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-10 py-4 rounded-xl font-bold text-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl inline-block"
                  >
                    📦 Practice Questions
                  </Link>
                  <Link
                    to="/ide"
                    className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white px-10 py-4 rounded-xl font-bold text-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl inline-block"
                  >
                    💻 Try Our IDE
                  </Link>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Footer */}
      <footer className="bg-gradient-to-r from-black/30 to-gray-900/30 border-t border-white/20 py-12 mt-20 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <YCodeThumbnail size="medium" />
            </div>
            <p className="text-gray-300 text-lg mb-3">
              Advanced coding platform for modern learners
            </p>
            <p className="text-orange-400 text-base font-semibold">
              🚀 Debugging the Road to Progress
            </p>
            <div className="mt-6 pt-6 border-t border-white/10">
              <p className="text-gray-400 text-sm">
                © 2024 YCode. Empowering developers worldwide.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Courses; 
